import React, { useState, useMemo, useEffect } from "react";

/**
 * BulkCancelSLModal
 *
 * Allows the user to pick specific SL orders to bulk-cancel, filtered by
 * Symbol (multi-select) and Strategy (multi-select).
 *
 * Props:
 *   isOpen          {boolean}   - Whether the modal is visible
 *   onClose         {Function}  - Called when the modal is dismissed
 *   orders          {Array}     - All live orders
 *   positions       {Array}     - All live positions (for SL detection)
 *   onCancelOrders  {Function}  - Receives the final list of orders to cancel
 *   assetFilterType {string}    - "positionSL" (CE/PE) | "pledgePlusPositionSL" (MCX/FUT)
 */

// ─── SL detection (mirrors GroupSelectionModal logic) ───────────────────────
function isSLOrder(order, allPositions) {
  const isPending =
    String(order.status_no) === "6" ||
    ["pending", "open", "trigger pending", "not sent"].includes(
      (order.status || "").toLowerCase()
    );
  if (!isPending) return false;

  const clientId = order.clientId || order.client_id;
  const clientPositions = (allPositions || []).filter((p) => {
    const pId = p.clientId || p.client_id;
    return String(pId).trim() === String(clientId).trim();
  });

  const relatedPos = clientPositions.find((p) => {
    const cleanPSym = (p.symbol || "")
      .toUpperCase()
      .replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "")
      .trim();
    const cleanOSym = (order.symbol || "")
      .toUpperCase()
      .replace(/^(MCX|NSE|NFO|BFO|CDS):/i, "")
      .trim();
    if (cleanPSym !== cleanOSym) return false;

    const pStrat = (p.strategy || "").toString().trim().toUpperCase();
    const oStrat = (order.strategy || "").toString().trim().toUpperCase();
    const pClean = pStrat === "N/A" ? "" : pStrat;
    const oClean = oStrat === "N/A" ? "" : oStrat;
    return pClean === oClean;
  });

  if (!relatedPos) return false;

  const netQty = parseFloat(relatedPos.net_qty || relatedPos.qty || 0);
  if (netQty === 0) return false;

  const sideStr = String(order.side || "").trim().toLowerCase();
  const isOrderBuy = sideStr === "1" || sideStr === "buy" || sideStr === "b";
  const isPosLong = netQty > 0;

  return (isPosLong && !isOrderBuy) || (!isPosLong && isOrderBuy);
}

// ─── Main Component ──────────────────────────────────────────────────────────
export function BulkCancelSLModal({
  isOpen,
  onClose,
  orders = [],
  positions = [],
  onCancelOrders,
  assetFilterType = "positionSL",
}) {
  // Multi-select state: null means "All selected"
  const [selectedSymbols, setSelectedSymbols] = useState(new Set());
  const [selectedStrategies, setSelectedStrategies] = useState(new Set());
  const [error, setError] = useState("");

  // ── Build pool of SL orders that match assetFilterType ──────────────────
  const slOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    // Index names for Optimus — only these CE/PE options are SL-managed
    const INDEX_NAMES = ["BANKNIFTY", "FINNIFTY", "MIDCPNIFTY", "NIFTY", "SENSEX", "BANKEX", "MIDCAPNIFTY"];

    return orders.filter((o) => {
      const sym = (o.symbol || "").toUpperCase();

      // Strip exchange prefix for matching
      const cleanSym = sym.replace(/^(NSE|BSE|NFO|BFO|MCX|CDS):/i, "");

      const isOption =
        sym.endsWith("CE") ||
        sym.endsWith("PE") ||
        sym.includes(" CE ") ||
        sym.includes(" PE ");

      const isMcxOrFut =
        sym.includes("MCX:") || sym.includes("FUT") || sym.endsWith("FUT");

      // For Optimus: must be a CE/PE option AND symbol must belong to a known index
      const isIndexOption =
        isOption &&
        INDEX_NAMES.some((idx) => cleanSym.startsWith(idx));

      if (assetFilterType === "positionSL") {
        if (!isIndexOption || isMcxOrFut) return false;
      } else if (assetFilterType === "pledgePlusPositionSL") {
        if (!isMcxOrFut || isOption) return false;
      }

      return isSLOrder(o, positions);
    });
  }, [orders, positions, assetFilterType]);


  // ── Derive unique symbols and strategies with counts ─────────────────────
  const { symbolOptions, strategyOptions } = useMemo(() => {
    const symMap = {};
    const stratMap = {};

    slOrders.forEach((o) => {
      const sym = (o.symbol || "").toUpperCase();
      symMap[sym] = (symMap[sym] || 0) + 1;

      const strat = o.strategy || o.order_tag || "Manual";
      stratMap[strat] = (stratMap[strat] || 0) + 1;
    });

    return {
      symbolOptions: Object.entries(symMap).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
      strategyOptions: Object.entries(stratMap).sort(([a], [b]) =>
        a.localeCompare(b)
      ),
    };
  }, [slOrders]);

  // ── Reset when modal opens — start with everything selected ──────────────
  useEffect(() => {
    if (isOpen) {
      setSelectedSymbols(new Set(symbolOptions.map(([s]) => s)));
      setSelectedStrategies(new Set(strategyOptions.map(([s]) => s)));
      setError("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    setError("");
  }, [selectedSymbols, selectedStrategies]);

  // ── Final filtered list — explicit set membership only ──────────────────
  const filteredOrders = useMemo(() => {
    return slOrders.filter((o) => {
      const sym = (o.symbol || "").toUpperCase();
      const strat = o.strategy || o.order_tag || "Manual";
      return selectedSymbols.has(sym) && selectedStrategies.has(strat);
    });
  }, [slOrders, selectedSymbols, selectedStrategies]);

  // ── Toggle helpers ───────────────────────────────────────────────────────
  const toggleSymbol = (sym) => {
    setSelectedSymbols((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else next.add(sym);
      return next;
    });
  };

  const toggleStrategy = (strat) => {
    setSelectedStrategies((prev) => {
      const next = new Set(prev);
      if (next.has(strat)) next.delete(strat);
      else next.add(strat);
      return next;
    });
  };

  const allSymsSelected = symbolOptions.length > 0 && selectedSymbols.size === symbolOptions.length;
  const allStratsSelected = strategyOptions.length > 0 && selectedStrategies.size === strategyOptions.length;

  const toggleAllSymbols = () => {
    if (allSymsSelected) {
      // Clear all
      setSelectedSymbols(new Set());
    } else {
      // Select all
      setSelectedSymbols(new Set(symbolOptions.map(([s]) => s)));
    }
  };

  const toggleAllStrategies = () => {
    if (allStratsSelected) {
      // Clear all
      setSelectedStrategies(new Set());
    } else {
      // Select all
      setSelectedStrategies(new Set(strategyOptions.map(([s]) => s)));
    }
  };

  const handleConfirm = () => {
    if (filteredOrders.length === 0) {
      setError("No SL orders match the selected criteria.");
      return;
    }
    onCancelOrders(filteredOrders);
    onClose();
  };

  if (!isOpen) return null;

  const typeLabel =
    assetFilterType === "pledgePlusPositionSL" ? "MCX/FUT" : "Options (CE/PE)";

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#14161f] border border-gray-700/80 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-red-950/60 to-[#1c2230] p-4 border-b border-red-900/40 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-7 bg-red-500 rounded-full" />
            <div>
              <h3 className="text-white font-bold text-lg leading-tight">
                Bulk Cancel SL Orders
              </h3>
              <p className="text-red-400/70 text-[11px] mt-0.5 font-medium uppercase tracking-wider">
                {typeLabel} · Stop-Loss Orders
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── No SL Orders Empty State ──────────────────────────────── */}
        {slOrders.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-center gap-3">
            <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center">
              <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-400 font-medium">No SL orders found</p>
            <p className="text-gray-600 text-xs max-w-xs">
              No pending stop-loss orders detected for current positions.
            </p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-gray-400 text-sm">
              Select symbols and strategies to filter which SL orders to cancel.
              <span className="text-gray-600 ml-1">(Empty selection = all)</span>
            </p>

            {/* ── Symbol Section ──────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">
                  Symbol
                </label>
                <button
                  onClick={toggleAllSymbols}
                  className="text-[10px] font-semibold text-indigo-400 hover:text-indigo-300 transition-colors uppercase tracking-wide"
                >
                  {allSymsSelected ? "Clear All" : "Select All"}
                </button>
              </div>
              <div className="bg-[#0f1116] border border-gray-800 rounded-lg divide-y divide-gray-800/60 max-h-44 overflow-y-auto">
                {symbolOptions.map(([sym, count]) => {
                  const checked = allSymsSelected || selectedSymbols.has(sym);
                  return (
                    <label
                      key={sym}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03] transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSymbol(sym)}
                        className="w-3.5 h-3.5 accent-red-500 cursor-pointer"
                      />
                      <span className="text-gray-200 text-xs font-mono flex-1 truncate">
                        {sym}
                      </span>
                      <span className="text-red-400 bg-red-900/20 text-[10px] font-bold px-1.5 py-0.5 rounded border border-red-800/30 shrink-0">
                        {count}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* ── Strategy Section ────────────────────────────────── */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-gray-400 text-xs font-bold uppercase tracking-wider">
                  Strategy
                </label>
                <button
                  onClick={toggleAllStrategies}
                  className="text-[10px] font-semibold text-indigo-400 hover:text-indigo-300 transition-colors uppercase tracking-wide"
                >
                  {allStratsSelected ? "Clear All" : "Select All"}
                </button>
              </div>
              <div className="bg-[#0f1116] border border-gray-800 rounded-lg divide-y divide-gray-800/60 max-h-36 overflow-y-auto">
                {strategyOptions.map(([strat, count]) => {
                  const checked =
                    allStratsSelected || selectedStrategies.has(strat);
                  return (
                    <label
                      key={strat}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03] transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStrategy(strat)}
                        className="w-3.5 h-3.5 accent-red-500 cursor-pointer"
                      />
                      <span className="text-gray-200 text-xs flex-1 truncate">
                        {strat}
                      </span>
                      <span className="text-orange-400 bg-orange-900/20 text-[10px] font-bold px-1.5 py-0.5 rounded border border-orange-800/30 shrink-0">
                        {count}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* ── Live Match Count ────────────────────────────────── */}
            <div className="bg-[#0f1116] border border-gray-800/60 rounded-lg px-4 py-3 flex items-center justify-between">
              <span className="text-gray-500 text-sm">Will cancel:</span>
              <span
                className={`font-bold text-sm px-3 py-1 rounded border ${
                  filteredOrders.length > 0
                    ? "text-red-400 bg-red-900/20 border-red-800/40"
                    : "text-gray-500 bg-gray-800/30 border-gray-700/30"
                }`}
              >
                {filteredOrders.length} SL order
                {filteredOrders.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* ── Error ───────────────────────────────────────────── */}
            {error && (
              <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-lg">
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-400 text-xs font-medium">{error}</span>
              </div>
            )}
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="p-4 bg-[#0f1116] border-t border-gray-800 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 transition-colors text-sm"
          >
            Close
          </button>
          {slOrders.length > 0 && (
            <button
              onClick={handleConfirm}
              disabled={filteredOrders.length === 0}
              className="flex-1 py-2 rounded-lg bg-red-700 hover:bg-red-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white font-bold shadow-lg shadow-red-900/40 transition-all text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel {filteredOrders.length} SL Order
              {filteredOrders.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default BulkCancelSLModal;
